import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Pagination,
  Badge,
  Alert,
} from "react-bootstrap";
import {
  getSalarybyid,
  getEmployeeById,
  addSalaryAPI,
  updateSalaryAPI,
  updateAdvanceSalaryGiven,
} from "../../helpers/api.ts";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UserDetails = () => {
  const { EmployeeID } = useParams();
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [selectedSalaryId, setSelectedSalaryId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [extraAdvance, setExtraAdvance] = useState("");
  const [formData, setFormData] = useState({
    SalaryID: "",
    EmployeeID: EmployeeID,
    Month: "",
    AdvanceSalaryGiven: "",
    LeaveBalance: "",
    LeaveTaken: "",
  });
  const [employeeName, setEmployeeName] = useState("Loading...");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchEmployeeData(EmployeeID);
    fetchSalaries(EmployeeID);
  }, [EmployeeID]);

  const fetchEmployeeData = async (id) => {
    try {
      const response = await getEmployeeById(id);
      if (response && response.data) {
        setEmployeeName(response.data.EmployeeName || "Employee Not Found");
        setEmployeeDetails(response.data);
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      setEmployeeName("Error loading employee");
    }
  };

  const fetchSalaries = async (id) => {
    try {
      const response = await getSalarybyid(id);
      setSalaries(response.data || []);
      setTotalPages(Math.ceil((response.data?.length || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching salaries:", error);
      setSalaries([]);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleModalShow = (isUpdate = false, salary = null) => {
    if (isUpdate && salary) {
      setFormData({
        SalaryID: salary.SalaryID,
        EmployeeID: salary.EmployeeID,
        Month: new Date(salary.Month).toISOString().slice(0, 7),
        AdvanceSalaryGiven: salary.AdvanceSalaryGiven || "",
        LeaveBalance: salary.LeaveBalance || "",
        LeaveTaken: salary.LeaveTaken || "",
      });
      setIsUpdateMode(true);
    } else {
      setFormData({
        SalaryID: "",
        EmployeeID: EmployeeID,
        Month: "",
        AdvanceSalaryGiven: "",
        LeaveBalance: "",
        LeaveTaken: "",
      });
      setIsUpdateMode(false);
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      const { SalaryID, EmployeeID, Month, AdvanceSalaryGiven, LeaveBalance, LeaveTaken } = formData;
      
      if (!Month) {
        toast.error("Please select a month");
        return;
      }

      const formattedMonth = new Date(Month).toISOString().slice(0, 7);

      if (isUpdateMode) {
        await updateSalaryAPI({
          SalaryID,
          EmployeeID,
          Month: formattedMonth,
          AdvanceSalaryGiven: Number(AdvanceSalaryGiven) || 0,
          LeaveBalance: Number(LeaveBalance) || 0,
          LeaveTaken: Number(LeaveTaken) || 0,
        });
        toast.success("Salary updated successfully!");
      } else {
        const duplicateSalary = salaries.find(
          (salary) => salary.Month.slice(0, 7) === formattedMonth
        );

        if (duplicateSalary) {
          toast.error(`Salary for ${formattedMonth} already exists!`);
          return;
        }

        await addSalaryAPI({
          EmployeeID,
          Month: formattedMonth,
          AdvanceSalaryGiven: Number(AdvanceSalaryGiven) || 0,
          LeaveBalance: Number(LeaveBalance) || 0,
          LeaveTaken: Number(LeaveTaken) || 0,
        });
        toast.success("Salary added successfully!");
      }

      fetchSalaries(EmployeeID);
      setShowModal(false);
    } catch (error) {
      console.error("Error saving salary:", error);
      toast.error("Failed to save salary data");
    }
  };

  const handleAdvanceSubmit = async () => {
    if (!extraAdvance || Number(extraAdvance) <= 0) {
      toast.error("Please enter a valid advance amount");
      return;
    }

    try {
      await updateAdvanceSalaryGiven({
        SalaryID: selectedSalaryId,
        newAdvance: Number(extraAdvance),
      });
      toast.success("Advance salary added successfully!");
      fetchSalaries(EmployeeID);
      setShowAdvanceModal(false);
      setExtraAdvance("");
    } catch (error) {
      console.error("Error updating advance:", error);
      toast.error("Failed to update advance salary");
    }
  };

  const paginateSalaries = (salaries, currentPage, itemsPerPage) => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return salaries.slice(start, end);
  };

  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMonth = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
    });
  };

  return (
    <Container className="px-3 py-4" style={{ marginTop: "70px", maxWidth: "1400px" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="mb-4">
        <Breadcrumbs title="Portal" breadcrumbItem="Employee Salary Details" />
      </div>

      {/* Employee Info Card */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h3 className="mb-1">{employeeName}</h3>
              {employeeDetails && (
                <div className="text-muted">
                  <small>
                    <strong>Role:</strong> {employeeDetails.role} | 
                    <strong className="ms-3">Basic Salary:</strong> {formatCurrency(employeeDetails.BasicSalary)} | 
                    <strong className="ms-3">Allowances:</strong> {formatCurrency(employeeDetails.Allowances)} | 
                    <strong className="ms-3">Deductions:</strong> {formatCurrency(employeeDetails.Deductions)}
                  </small>
                </div>
              )}
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate("/users")}
                className="me-2"
              >
                <i className="mdi mdi-arrow-left me-1"></i> Back
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleModalShow(false)}
              >
                <i className="mdi mdi-plus me-1"></i> Add Salary
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Salary Records */}
      {salaries.length === 0 ? (
        <Alert variant="info" className="text-center">
          <i className="mdi mdi-information-outline me-2"></i>
          No salary records found. Click "Add Salary" to create the first record.
        </Alert>
      ) : (
        <>
          <Row>
            {paginateSalaries(salaries, currentPage, itemsPerPage).map((salary) => (
              <Col lg={12} key={salary.SalaryID} className="mb-3">
                <Card className="shadow-sm h-100">
                  <Card.Body>
                    <Row>
                      {/* Month Header */}
                      <Col xs={12} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h5 className="mb-0 text-primary">
                              <i className="mdi mdi-calendar-month me-2"></i>
                              {formatMonth(salary.Month)}
                            </h5>
                          </div>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleModalShow(true, salary)}
                          >
                            <i className="mdi mdi-pencil me-1"></i> Edit
                          </Button>
                        </div>
                      </Col>

                      {/* Salary Breakdown */}
                      <Col md={3} className="mb-3">
                        <div className="border rounded p-3 h-100 bg-light">
                          <small className="text-muted d-block mb-1">Total Salary</small>
                          <h5 className="mb-0 text-success">{formatCurrency(salary.TotalSalary)}</h5>
                        </div>
                      </Col>

                      <Col md={3} className="mb-3">
                        <div className="border rounded p-3 h-100">
                          <small className="text-muted d-block mb-1">
                            Advance Given
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2"
                              onClick={() => {
                                setSelectedSalaryId(salary.SalaryID);
                                setShowAdvanceModal(true);
                              }}
                              title="Add more advance"
                            >
                              <i className="mdi mdi-plus-circle text-success"></i>
                            </Button>
                          </small>
                          <h5 className="mb-0 text-warning">{formatCurrency(salary.AdvanceSalaryDeduction)}</h5>
                        </div>
                      </Col>

                      <Col md={3} className="mb-3">
                        <div className="border rounded p-3 h-100">
                          <small className="text-muted d-block mb-1">
                            Leaves Taken ({salary.LeaveTaken} days)
                          </small>
                          <h5 className="mb-0 text-danger">{formatCurrency(salary.LeaveDeduction)}</h5>
                        </div>
                      </Col>

                      <Col md={3} className="mb-3">
                        <div className="border rounded p-3 h-100 bg-success-subtle">
                          <small className="text-muted d-block mb-1">
                            <strong>Salary Paid</strong>
                          </small>
                          <h5 className="mb-0 text-success">
                            <strong>{formatCurrency(salary.SalaryPaid)}</strong>
                          </h5>
                        </div>
                      </Col>

                      {/* Additional Info */}
                      <Col xs={12}>
                        <div className="d-flex flex-wrap gap-3 pt-2 border-top">
                          <small className="text-muted">
                            <strong>After Advance:</strong> {formatCurrency(salary.SalaryAfterAdvance)}
                          </small>
                          <small className="text-muted">
                            <strong>Pending:</strong> {formatCurrency(salary.PendingSalary)}
                          </small>
                          <small className="text-muted">
                            <strong>Leave Balance:</strong> {salary.LeaveBalance} days
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.Prev
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                />
                {[...Array(totalPages)].map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Salary Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="mdi mdi-cash-multiple me-2"></i>
            {isUpdateMode ? "Update" : "Add"} Salary Record
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Month <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="month"
                    name="Month"
                    value={formData.Month}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Advance Salary Given</Form.Label>
                  <Form.Control
                    type="number"
                    name="AdvanceSalaryGiven"
                    value={formData.AdvanceSalaryGiven}
                    onChange={handleFormChange}
                    placeholder="0"
                    min="0"
                  />
                  <Form.Text className="text-muted">
                    Total advance paid to employee this month
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Leave Taken (Days)</Form.Label>
                  <Form.Control
                    type="number"
                    name="LeaveTaken"
                    value={formData.LeaveTaken}
                    onChange={handleFormChange}
                    placeholder="0"
                    min="0"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Leave Balance (Days)</Form.Label>
                  <Form.Control
                    type="number"
                    name="LeaveBalance"
                    value={formData.LeaveBalance}
                    onChange={handleFormChange}
                    placeholder="0"
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            <i className="mdi mdi-content-save me-1"></i>
            {isUpdateMode ? "Update" : "Add"} Salary
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Extra Advance Modal */}
      <Modal
        show={showAdvanceModal}
        onHide={() => {
          setShowAdvanceModal(false);
          setExtraAdvance("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="mdi mdi-cash-plus me-2"></i>
            Add Extra Advance
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Extra Advance Amount</Form.Label>
            <Form.Control
              type="number"
              value={extraAdvance}
              onChange={(e) => setExtraAdvance(e.target.value)}
              placeholder="Enter amount"
              min="1"
              autoFocus
            />
            <Form.Text className="text-muted">
              This will be added to the existing advance deduction
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAdvanceModal(false);
              setExtraAdvance("");
            }}
          >
            Cancel
          </Button>
          <Button variant="success" onClick={handleAdvanceSubmit}>
            <i className="mdi mdi-check me-1"></i>
            Add Advance
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UserDetails;



// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import {
//   Container,
//   Table,
//   Button,
//   Modal,
//   Form,
//   Pagination,
// } from "react-bootstrap";
// import {
//   getSalarybyid,
//   getEmployeeById,
//   addSalaryAPI,
//   updateSalaryAPI,
//   updateAdvanceSalaryGiven,
// } from "../../helpers/api.ts";
// import Breadcrumbs from "../../components/Common/Breadcrumb.js";
// import { toast, ToastContainer } from "react-toastify";

// import "react-toastify/dist/ReactToastify.css";

// const UserDetails = () => {
//   const { EmployeeID } = useParams();
//   const navigate = useNavigate();
//   const [salaries, setSalaries] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [showExtraAdvModal, setShowExtraAdvModal] = useState(false);
//   const [salId, setSalId] = useState(false);
//   const [isUpdateMode, setIsUpdateMode] = useState(false);
//   const [extraAdvance, setExtraAdvance] = useState(0);
//   const [formData, setFormData] = useState({
//     // SalaryID: "",
//     // EmployeeID: EmployeeID,
//     // Month: "",
//     // AdvanceSalaryGiven: 0,
//     // LeaveBalance: 0,
//     // LeaveTaken: 0,
//   });
//   const [employeeName, setEmployeeName] = useState("Fetching...");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalPages, setTotalPages] = useState(1);
//   const itemsPerPage = 10;

//   useEffect(() => {
//     fetchEmployeeName(EmployeeID);
//     fetchSalaries(EmployeeID);
//   }, [EmployeeID, currentPage, showExtraAdvModal]);

//   const fetchEmployeeName = async (id) => {
//     try {
//       const response = await getEmployeeById(id);
//       if (response && response.data) {
//         setEmployeeName(response.data.EmployeeName || "Employee Not Found");
//       } else {
//         setEmployeeName("Employee Not Found");
//       }
//     } catch (error) {
//       console.error("Error fetching employee name:", error);
//       setEmployeeName("Error fetching employee details");
//     }
//   };

//   const fetchSalaries = async (id) => {
//     try {
//       const response = await getSalarybyid(id);
//       setSalaries(response.data);
//       const totalSalaries = response.data.length;
//       setTotalPages(Math.ceil(totalSalaries / itemsPerPage));
//     } catch (error) {
//       console.error("Error fetching salaries:", error);
//       setSalaries([]);
//     }
//   };

//   const handleFormChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };
//   useEffect(() => {
//     console.log(formData, "Updated formData");
//   }, [formData]); // Logs when formData changes
//   const handleModalShow = (isUpdate, salary) => {
//     if (isUpdate) {
//       console.log("Opening update modal with salary data:", salary); // Debugging log
//       setFormData({
//         SalaryID: salary.SalaryID,
//         EmployeeID: salary.EmployeeID,
//         Month: new Date(salary.Month).toISOString().slice(0, 7),
//         AdvanceSalaryGiven: String(salary.AdvanceSalaryGiven) || 0, // Ensure number
//         LeaveBalance: Number(salary.LeaveBalance) || 0, // Ensure number
//         LeaveTaken: Number(salary.LeaveTaken) || 0, // Ensure number
//       });
//       setIsUpdateMode(true);
//     } else {
//       setFormData({
//         SalaryID: "",
//         EmployeeID: EmployeeID,
//         Month: "",
//         AdvanceSalaryGiven: 0,
//         LeaveBalance: 0,
//         LeaveTaken: 0,
//       });
//       setIsUpdateMode(false);
//     }
//     setShowModal(true);
//   };

//   const handleSubmit = async () => {
//     try {
//       const {
//         SalaryID,
//         EmployeeID,
//         Month,
//         AdvanceSalaryGiven,
//         LeaveBalance,
//         LeaveTaken,
//       } = formData;
//       const formattedMonth = new Date(Month).toISOString().slice(0, 7);

//       if (isUpdateMode) {
//         await updateSalaryAPI({
//           SalaryID,
//           EmployeeID,
//           Month: formattedMonth,
//           AdvanceSalaryGiven,
//           LeaveBalance,
//           LeaveTaken,
//         });
//         toast.success("Salary updated successfully!");
//       } else {
//         const duplicateSalary = salaries.find(
//           (salary) => salary.Month.slice(0, 7) === formattedMonth
//         );

//         if (duplicateSalary) {
//           toast.error(`Salary for ${formattedMonth} is already added.`);
//           return;
//         }

//         await addSalaryAPI({
//           EmployeeID,
//           Month: formattedMonth,
//           AdvanceSalaryGiven,
//           LeaveBalance,
//           LeaveTaken,
//         });
//         toast.success("Salary added successfully!");
//       }

//       fetchSalaries(EmployeeID);
//       setShowModal(false);
//     } catch (error) {
//       console.error("Error saving salary data:", error);
//       toast.error("Error while saving salary data");
//     }
//   };
//   const handleExtraAdvSubmit = async () => {
//     try {
//       await updateAdvanceSalaryGiven({
//         SalaryID: salId,
//         newAdvance: extraAdvance,
//       });
//       toast.success("Salary updated successfully!");
//     } catch (error) {
//       toast.error("Error while updating Advance salary data");
//     } finally {
//       fetchSalaries();
//       setShowExtraAdvModal(false);
//     }
//   };

//   const paginateSalaries = (salaries, currentPage, itemsPerPage) => {
//     const start = (currentPage - 1) * itemsPerPage;
//     const end = start + itemsPerPage;
//     return salaries.slice(start, end);
//   };

//   const handlePageChange = (page) => {
//     if (page >= 1 && page <= totalPages) {
//       setCurrentPage(page);
//     }
//   };

//   return (
//     <Container className="container px-3 px-md-5" style={{ marginTop: "90px" }}>
//       <h6 className="mt-5">
//         <Breadcrumbs title="Portal" breadcrumbItem="Salary-details" />
//       </h6>
//       <div
//         className="d-flex justify-content-between flex-column flex-md-row align-items-start align-items-md-center mb-3 gap-2"
//       >
//         <h2 className="mb-0">{employeeName}</h2>
//         <span onClick={() => navigate("/users")} className="text-primary" style={{cursor: "pointer"}}>Back</span>
//       </div>
//       <div
//         className="d-flex justify-content-end mb-3"
//       >
//         <Button onClick={() => handleModalShow(false)} className="w-fit w-md-auto">Add Salary</Button>
//       </div>

//       <div className="table-responsive">
//       <Table striped bordered hover className="mt-3">
//         <thead>
//           <tr>
//             <th>#</th>
//             <th>Month</th>
//             <th>Total Salary</th>
//             <th>Advance Deduction</th>
//             <th>After Advance</th>
//             <th>Leave Deduction</th>
//             <th>Salary Paid</th>
//             <th>Pending Salary</th>
//             <th>Advance Salary Given</th>
//             <th>Leave Balance</th>
//             <th>Leave Taken</th>
//             <th>Actions</th>
//           </tr>
//         </thead>
//         <tbody>
//           {salaries.length === 0 ? (
//             <tr>
//               <td colSpan="12" className="text-center">
//                 No salary data available
//               </td>
//             </tr>
//           ) : (
//             paginateSalaries(salaries, currentPage, itemsPerPage).map(
//               (salary, index) => (
//                 <tr key={salary.SalaryID}>
//                   <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                   <td>
//                     {new Date(salary.Month).toLocaleDateString("en-GB", {
//                       year: "numeric",
//                       month: "short",
//                     })}
//                   </td>
//                   <td>{salary.TotalSalary?.toFixed(2)}</td>
//                   <td>
//                     {salary.AdvanceSalaryDeduction?.toFixed(2)}{" "}
//                     <i
//                       className="fa fa-plus text-success cursor-pointer"
//                       onClick={() => {
//                         setShowExtraAdvModal(true);
//                         setSalId(salary.SalaryID);
//                       }}
//                     ></i>{" "}
//                   </td>
//                   <td>{salary.SalaryAfterAdvance?.toFixed(2)}</td>
//                   <td>{salary.LeaveDeduction?.toFixed(2)}</td>
//                   <td>{salary.SalaryPaid?.toFixed(2)}</td>
//                   <td>{salary.PendingSalary?.toFixed(2)}</td>
//                   <td>{salary.AdvanceSalaryGiven}</td>
//                   <td>{salary.LeaveBalance}</td>
//                   <td>{salary.LeaveTaken}</td>
//                   <td>
//                     <Button
//                       variant="warning"
//                       size="sm"
//                       onClick={() => handleModalShow(true, salary)}
//                       className="w-100 w-sm-auto"
//                     >
//                       Update
//                     </Button>
//                   </td>
//                 </tr>
//               )
//             )
//           )}
//         </tbody>
//       </Table>
//       </div>

//       <div className="d-flex justify-content-center mt-3">
//         <Pagination>
//           <Pagination.Prev
//             onClick={() => handlePageChange(currentPage - 1)}
//             disabled={currentPage === 1}
//           />
//           {[...Array(totalPages)].map((_, index) => (
//             <Pagination.Item
//               key={index + 1}
//               active={index + 1 === currentPage}
//               onClick={() => handlePageChange(index + 1)}
//             >
//               {index + 1}
//             </Pagination.Item>
//           ))}
//           <Pagination.Next
//             onClick={() => handlePageChange(currentPage + 1)}
//             disabled={currentPage === totalPages}
//           />
//         </Pagination>
//       </div>
//       <Modal show={showModal} onHide={() => setShowModal(false)} className="modal-fullscreen-md-down">
//         <Modal.Header closeButton>
//           <Modal.Title>
//             {isUpdateMode ? "Update Salary" : "Add Salary"}
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Month</Form.Label>
//               <Form.Control
//                 type="month"
//                 name="Month"
//                 value={formData.Month}
//                 onChange={handleFormChange}
//               />
//             </Form.Group>
//             <Form.Group className="mb-3">
//               <Form.Label>Advance Salary Given</Form.Label>
//               <Form.Control
//                 type="number"
//                 name="AdvanceSalaryGiven"
//                 value={formData.AdvanceSalaryGiven}
//                 onChange={handleFormChange}
//               />
//             </Form.Group>
//             <Form.Group className="mb-3">
//               <Form.Label>Leave Balance</Form.Label>
//               <Form.Control
//                 type="number"
//                 name="LeaveBalance"
//                 value={formData.LeaveBalance}
//                 onChange={handleFormChange}
//               />
//             </Form.Group>
//             <Form.Group className="mb-3">
//               <Form.Label>Leave Taken</Form.Label>
//               <Form.Control
//                 type="number"
//                 name="LeaveTaken"
//                 value={formData.LeaveTaken}
//                 onChange={handleFormChange}
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button variant="secondary" onClick={() => setShowModal(false)}>
//             Close
//           </Button>
//           <Button variant="primary" onClick={handleSubmit}>
//             Save
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       <Modal
//         show={showExtraAdvModal}
//         onHide={() => setShowExtraAdvModal(false)}
//         className="modal-fullscreen-md-down"
//       >
//         <Modal.Header closeButton>
//           <Modal.Title>
//             {isUpdateMode ? "Update Salary" : "Add Salary"}
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form>
//             <Form.Group className="mb-3">
//               <Form.Label>Extra Advance</Form.Label>
//               <Form.Control
//                 type="number"
//                 name="LeaveTaken"
//                 value={extraAdvance}
//                 onChange={(e) => setExtraAdvance(e.target.value)}
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer>
//           <Button
//             variant="secondary"
//             onClick={() => setShowExtraAdvModal(false)}
//           >
//             Close
//           </Button>
//           <Button variant="primary" onClick={handleExtraAdvSubmit}>
//             Save
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       <ToastContainer />
//     </Container>
//   );
// };

// export default UserDetails;